// @ts-nocheck
import type { User, Workspace, Board, BoardData, Group, Column, Item, Folder, BoardShare, OrgMember, Invite } from './types';

const BASE = '/api/monday';

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...opts?.headers },
    ...opts,
  });
  if (res.status === 401) {
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error || res.statusText);
  }
  return res.json();
}

// Setup
export const getSetupStatus = () => request<{ needsSetup: boolean; orgName: string | null }>('/setup/status');
export const initSetup = (data: { orgName: string; adminEmail: string; authMethod: string; password?: string }) =>
  request<{ ok: boolean; totp_secret?: string; totp_uri?: string; existing_user?: boolean }>('/setup/init', { method: 'POST', body: JSON.stringify(data) });
export const verifySetupTOTP = (code: string) =>
  request<{ ok: boolean }>('/setup/verify-totp', { method: 'POST', body: JSON.stringify({ code }) });

// Auth
export const getMe = () => request<{ user: User }>('/auth/me');
export const logout = () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' });
export const loginWithPassword = (email: string, password: string) =>
  request<{ requires_totp: boolean }>('/auth/login/password', { method: 'POST', body: JSON.stringify({ email, password }) });
export const verifyLoginTOTP = (code: string) =>
  request<{ ok: boolean }>('/auth/login/totp', { method: 'POST', body: JSON.stringify({ code }) });

// Invites
export const createInvite = (data: { email: string; role: string; can_share_external: boolean }) =>
  request<{ invite_link: string; invite: Invite }>('/invites', { method: 'POST', body: JSON.stringify(data) });
export const getInvites = () => request<{ invites: Invite[] }>('/invites');
export const deleteInvite = (id: string) => request<{ ok: boolean }>(`/invites/${id}`, { method: 'DELETE' });
export const validateInvite = (token: string) =>
  request<{ valid: boolean; email?: string; org_name?: string; reason?: string }>(`/invites/validate/${token}`);
export const acceptInvite = (token: string, data: { name: string; authMethod: string; password?: string }) =>
  request<{ ok: boolean; totp_secret?: string; totp_uri?: string; redirect?: string }>(`/invites/accept/${token}`, { method: 'POST', body: JSON.stringify(data) });
export const verifyInviteTOTP = (token: string, code: string) =>
  request<{ ok: boolean }>(`/invites/accept/${token}/verify-totp`, { method: 'POST', body: JSON.stringify({ code }) });

// Org management
export const getOrg = () => request<{ org: any; members: OrgMember[] }>('/org');
export const updateOrg = (data: { name: string }) =>
  request<{ ok: boolean }>('/org', { method: 'PUT', body: JSON.stringify(data) });
export const updateOrgMember = (userId: string, data: { role?: string; can_share_external?: number }) =>
  request<{ ok: boolean }>(`/org/members/${userId}`, { method: 'PUT', body: JSON.stringify(data) });
export const removeOrgMember = (userId: string) =>
  request<{ ok: boolean }>(`/org/members/${userId}`, { method: 'DELETE' });

// Global search
export const globalSearch = (q: string) =>
  request<{ results: { item_id: string; item_name: string; board_id: string; board_name: string; workspace_id: string }[] }>(`/search?q=${encodeURIComponent(q)}`);

// Workspaces
export const getWorkspaces = () => request<{ workspaces: Workspace[] }>('/workspaces');
export const createWorkspace = (name: string) =>
  request<Workspace>('/workspaces', { method: 'POST', body: JSON.stringify({ name }) });
export const inviteMember = (wsId: string, email: string, role = 'member') =>
  request<{ ok: boolean }>(`/workspaces/${wsId}/members`, {
    method: 'POST', body: JSON.stringify({ email, role }),
  });

// Folders
export const getFolders = (wsId: string) =>
  request<{ folders: Folder[] }>(`/workspaces/${wsId}/folders`);
export const createFolder = (workspace_id: string, name: string) =>
  request<Folder>('/folders', { method: 'POST', body: JSON.stringify({ workspace_id, name }) });
export const updateFolder = (id: string, data: Partial<Folder>) =>
  request<{ ok: boolean }>(`/folders/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteFolder = (id: string) =>
  request<{ ok: boolean }>(`/folders/${id}`, { method: 'DELETE' });

// Boards
export const getBoards = (wsId: string) =>
  request<{ boards: Board[] }>(`/workspaces/${wsId}/boards`);
export const createBoard = (workspace_id: string, name: string, folder_id?: string) =>
  request<Board>('/boards', { method: 'POST', body: JSON.stringify({ workspace_id, name, folder_id }) });
export const updateBoard = (id: string, data: Partial<Board>) =>
  request<{ ok: boolean }>(`/boards/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBoard = (id: string) =>
  request<{ ok: boolean }>(`/boards/${id}`, { method: 'DELETE' });
export const duplicateBoard = (id: string) =>
  request<Board>(`/boards/${id}/duplicate`, { method: 'POST' });
export const getBoardData = (id: string) =>
  request<BoardData>(`/boards/${id}/data`);

// Board sharing
export const getBoardShares = (boardId: string) =>
  request<{ shares: BoardShare[] }>(`/boards/${boardId}/shares`);
export const shareBoardWithEmail = (boardId: string, email: string, role = 'editor') =>
  request<BoardShare>(`/boards/${boardId}/shares`, { method: 'POST', body: JSON.stringify({ email, role }) });
export const removeBoardShare = (boardId: string, shareId: string) =>
  request<{ ok: boolean }>(`/boards/${boardId}/shares/${shareId}`, { method: 'DELETE' });

// Export
export const exportBoardData = (id: string) =>
  request<BoardData>(`/boards/${id}/export`);

// Groups
export const createGroup = (board_id: string, name: string, color?: string, position?: number) =>
  request<Group>('/groups', { method: 'POST', body: JSON.stringify({ board_id, name, color, position }) });
export const updateGroup = (id: string, data: Partial<Group>) =>
  request<{ ok: boolean }>(`/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteGroup = (id: string) =>
  request<{ ok: boolean }>(`/groups/${id}`, { method: 'DELETE' });

// Columns
export const createColumn = (board_id: string, name: string, type: string, config?: any, position?: number) =>
  request<Column>('/columns', { method: 'POST', body: JSON.stringify({ board_id, name, type, config, position }) });
export const updateColumn = (id: string, data: Partial<Column>) =>
  request<{ ok: boolean }>(`/columns/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteColumn = (id: string) =>
  request<{ ok: boolean }>(`/columns/${id}`, { method: 'DELETE' });
export const cloneColumn = (id: string) =>
  request<Column>(`/columns/${id}/clone`, { method: 'POST' });

// Items
export const createItem = (group_id: string, board_id: string, name: string, position?: number) =>
  request<Item>('/items', { method: 'POST', body: JSON.stringify({ group_id, board_id, name, position }) });
export const updateItem = (id: string, data: Partial<Item>) =>
  request<{ ok: boolean }>(`/items/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteItem = (id: string) =>
  request<{ ok: boolean }>(`/items/${id}`, { method: 'DELETE' });
export const duplicateItem = (id: string) =>
  request<Item>(`/items/${id}/duplicate`, { method: 'POST' });

// Cells
export const updateCells = (updates: { item_id: string; column_id: string; value: any }[]) =>
  request<{ ok: boolean }>('/cells', { method: 'PUT', body: JSON.stringify({ updates }) });

// Import
export const importExcel = (data: {
  board_id: string; group_id: string;
  column_mapping: { excel_col: number; board_col_id: string }[];
  rows: any[][];
}) => request<{ ok: boolean; imported: number }>('/import/excel', { method: 'POST', body: JSON.stringify(data) });
